import { useCallback, useState, useRef, useContext } from 'react';
import { throttle } from 'lodash-es';
import { useAppSelector } from './useAppSelector';
import { fetchSuburbSuggestionsFromRex } from 'Api/Rex/rexApi';
import { fetchSuburbSuggestions } from 'Api/RpData/rpDataApi';
import { fetchSuburbSuggestionsFromGoogle } from 'utils/fetchSuburbSuggestionsFromGoogle';
import { AustralianState } from '@urbanx/agx-ui-components';
import { SuburbSuggestions } from '@urbanx/agx-ui-components/dist/types/components/portal/PropertyAddressForm/SuburbAutoComplete/SuburbAutoComplete';
import { AuthContext } from '@urbanx/agx.auth.client';

const useSuburbSuggestions = () => {
  const [loading, setLoading] = useState(false);
  const [suburbSuggestions, setSuburbSuggestions] = useState<
    SuburbSuggestions[]
  >([]);
  const ongoingRequests = useRef<AbortController[]>([]);
  const { getAuthToken } = useContext(AuthContext);
  const selectedForm = useAppSelector(state => state.form.selectedForm);
  const getSuburbSuggestions = useCallback(
    throttle(async (searchQuery?: string) => {
      if (!searchQuery || searchQuery.length < 3) {
        setSuburbSuggestions([]);
        return;
      }

      if (!selectedForm) return;

      setLoading(true);

      const controller = new AbortController();
      try {
        ongoingRequests.current.forEach((controller: AbortController) =>
          controller.abort()
        );
        ongoingRequests.current = [];

        ongoingRequests.current.push(controller);

        const authToken = await getAuthToken();
        if (!authToken) return;

        try {
          const result = await fetchSuburbSuggestionsFromRex(
            authToken,
            searchQuery,
            selectedForm.state,
            controller.signal
          );

          if (result && result.length > 0) {
            setSuburbSuggestions(
              result.map(suggestion => ({
                state: suggestion.stateOrRegion as AustralianState,
                postcode: suggestion.postCode,
                suggestionLine1: suggestion.suburbOrTown,
                suggestionLine2: `${suggestion.stateOrRegion} ${suggestion.postCode}`,
              }))
            );
          }
        } catch (rexError) {
          console.log('Rex API failed, falling back to RPData API:', rexError);

          try {
            // If REX API fails, falling back to I (RPData API)
            const result = await fetchSuburbSuggestions(
              authToken,
              searchQuery,
              selectedForm.state,
              controller.signal
            );

            if (result?.suburbSuggestions) {
              setSuburbSuggestions(
                result.suburbSuggestions.map(suggestion => ({
                  state: suggestion.state as unknown as AustralianState,
                  postcode: suggestion.postcode,
                  suggestionLine1: suggestion.suggestionLine1,
                  suggestionLine2: suggestion.suggestionLine2,
                }))
              );
            }
          } catch (rpDataerror) {
            console.log(
              'RPData API failed, falling back to Google Places API:',
              rpDataerror
            );

            try {
              // If both (REX & RPData APIs) fail, falling back to  (Google Places API)
              const result = await fetchSuburbSuggestionsFromGoogle(
                searchQuery,
                selectedForm.state
              );

              if (result && result.length > 0) {
                setSuburbSuggestions(
                  result
                    .map(suggestion => ({
                      state: suggestion.stateOrRegion as AustralianState,
                      postcode: suggestion.postCode,
                      suggestionLine1: suggestion.suburbOrTown,
                      suggestionLine2: `${suggestion.stateOrRegion} ${suggestion.postCode}`,
                    }))
                    .filter(suggestion => suggestion.postcode) //Some suburbs don't have postcodes so filtering here
                );
              }
            } catch (googlePlacesApiError) {
              console.log('Google Places API failed', googlePlacesApiError);
            }
          }
        }
      } catch (error) {
        console.error('Unexpected error:', error);
        setSuburbSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 100),
    []
  );

  return { suburbSuggestions, loading, getSuburbSuggestions };
};

export default useSuburbSuggestions;
